/**
 * In-app announcement session helpers.
 *
 * The announcements themselves live in `auth.announcements` and are
 * written and published by an admin — see `useLiveAnnouncements` in
 * `src/api/announcements.ts`, and `admin_announcements.py` in the sales
 * repo.
 *
 * They used to live here as a static array, which made publishing a side
 * effect of a deploy: the entry went in front of every user the moment
 * the release landed, with no approval, no preview, and no way to stop
 * it without shipping again. Interrupting the whole team should be a
 * decision somebody makes, not a consequence of merging.
 *
 * What is left here is the part that is genuinely the browser's: which
 * announcement this tab has already surfaced, and picking one from a
 * list the API has already narrowed to what is live.
 */

import type { Announcement } from '../api/announcements';

/**
 * Everything this person has not seen yet, newest first.
 *
 * The API returns only what is published and inside its date window, so
 * all that is left is to drop the ones they have already closed.
 *
 * They are shown as one paged surface rather than a stack — several
 * banners above the page turns a header into a wall, and several popups
 * in a row is how people learn to close them unread.
 */
export function pickAnnouncements(
  live: readonly Announcement[],
  acknowledgedKeys: readonly string[],
): Announcement[] {
  const seen = new Set(acknowledgedKeys);
  return live.filter((a) => !seen.has(a.key));
}

/**
 * Marks that an announcement has already been surfaced in this sign-in
 * session, so it does not reappear on every route change and reload
 * while the acknowledgement write is in flight — or forever, if that
 * write keeps failing.
 *
 * `sessionStorage` is scoped to the browser TAB, not to the sign-in, so
 * it survives a logout and a fresh login in the same tab. `AuthContext`
 * therefore clears it on both, which is what makes "once per session"
 * mean once per sign-in rather than once per tab.
 */
const SURFACED_KEY = 'hy.announcement.surfaced';

export function readSurfacedAnnouncement(): string {
  try {
    return window.sessionStorage.getItem(SURFACED_KEY) ?? '';
  } catch {
    // Private mode / storage disabled. Falling back to "not surfaced"
    // means it may show twice — better than never showing.
    return '';
  }
}

export function markAnnouncementSurfaced(key: string): void {
  try {
    window.sessionStorage.setItem(SURFACED_KEY, key);
  } catch {
    // Nothing to do; see readSurfacedAnnouncement.
  }
}

/** Called on sign-in and sign-out so a new session starts clean. */
export function clearAnnouncementSession(): void {
  try {
    window.sessionStorage.removeItem(SURFACED_KEY);
  } catch {
    // Nothing to do.
  }
}

/** Today as `YYYY-MM-DD` in the viewer's own timezone. */
export function todayIso(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
